/**
 * KPI Dashboard data helpers
 *
 * Data source: localStorage (synced via "Sync from Sheets" in the dashboard UI).
 * If no data has been synced yet, all helpers return empty results.
 */

import type {
  KpiWeeklyEntry,
  KpiDashboardData,
  SelectedPeriod,
  WeeklyStationMetric,
  AnomalyWeekly,
} from "../../types/kpiDashboard";
import { getKpiDataFromStorage } from "./KpiStorage";

const stored   = getKpiDataFromStorage();
const loadWeeks = (): KpiWeeklyEntry[] => stored?.entries ?? [];
export const getSyncedAt = (): string => stored?.syncedAt ?? "";

const ALL_WEEKS: KpiWeeklyEntry[] = loadWeeks().sort(
  (a, b) => a.year !== b.year ? a.year - b.year : a.week - b.week
);

const periodLabel = (year: number, week: number): string =>
  `${String(year).slice(-2)}-${String(week).padStart(2, "0")}`;

const weekLabel = (week: number): string =>
  `KW${String(week).padStart(2, "0")}`;

const toWeekMetric = (e: KpiWeeklyEntry): WeeklyStationMetric => ({
  ...e.stations,
  period: periodLabel(e.year, e.week),
});

/** Returns all available {year, week} pairs, newest first. */
export const getAvailablePeriods = (): SelectedPeriod[] =>
  [...ALL_WEEKS].reverse().map(({ year, week }) => ({ year, week }));

/** Builds the full KpiDashboardData for a given week. */
export const getKpiForPeriod = async (
  year: number,
  week: number
): Promise<KpiDashboardData> => {
  const selectedIdx = ALL_WEEKS.findIndex(
    (e) => e.year === year && e.week === week
  );
  if (selectedIdx === -1) {
    throw new Error(`Period ${year}-W${week} not found in synced data`);
  }

  // All entries up to and including the selected week
  const upTo = ALL_WEEKS.slice(0, selectedIdx + 1);

  // Last 12 entries for trend charts
  const trendWindow = upTo.slice(-12);

  const weeklyTrend: WeeklyStationMetric[] = trendWindow.map(toWeekMetric);

  const anomalyTrend: AnomalyWeekly[] = trendWindow
    .filter((e) => e.anomaly !== null)
    .map((e) => ({
      week: weekLabel(e.week),
      partnerLeer: e.anomaly!.partnerLeer,
      noConsumption: e.anomaly!.noConsumption,
      meterStop: e.anomaly!.meterStop,
      total: e.anomaly!.total,
    }));

  const invoiceTrend = trendWindow
    .filter((e) => e.invoice !== null)
    .map((e) => ({
      week: weekLabel(e.week),
      amount: e.invoice!.amount,
    }));

  const selected = ALL_WEEKS[selectedIdx];
  const prevEntry = selectedIdx > 0 ? ALL_WEEKS[selectedIdx - 1] : null;

  // Marketing: prefer the selected year's data; fall back to the latest available.
  const marketingForYear = [...ALL_WEEKS]
    .filter(e => e.year === year)
    .reverse()
    .find(e => e.marketing)?.marketing;

  const latestMarketing = [...ALL_WEEKS].reverse().find(e => e.marketing)?.marketing;

  return {
    weeklyTrend,
    anomalyTrend,
    invoiceTrend,
    currentWeek: toWeekMetric(selected),
    prevWeek: prevEntry ? toWeekMetric(prevEntry) : null,
    // overdueInvoices and failures are point-in-time snapshots — only show
    // data that actually belongs to the selected week. No fallback to avoid
    // showing stale data from a different week.
    overdueInvoices: (selected.overdueInvoices ?? [])
      .slice()
      .sort((a, b) => b.daysOverdue - a.daysOverdue),
    failures: selected.failures ?? [],
    marketing: selected.marketing
      ?? marketingForYear
      ?? latestMarketing
      ?? { adsCostThisMonth: 0, adsCostPrevMonth: 0, totalMarketingCost: 0 },
    lastUpdated: getSyncedAt(),
  };
};
